import { useCallback, useEffect, useRef } from 'react';

/**
 * Auto-scroll "pegado al fondo" para un hilo de chat.
 *
 * Extraído de `AIAssistant`, donde llevaba tiempo funcionando bien. Estaba
 * copiado a mano en tres sitios y en un cuarto —`AIPanel`, el panel de ARIA que
 * se abre desde la cabecera en toda la app— directamente NO estaba: su
 * contenedor no tenía ni `ref`, así que al enviar un mensaje el hilo se quedaba
 * arriba del todo y el usuario no veía ni lo que acababa de escribir ni la
 * respuesta. Medido: `scrollTop` 0 sobre 901 px de alto.
 *
 * Tres comportamientos, y los tres importan:
 *
 *  - **Pegado**: solo baja si el usuario ya estaba al fondo (margen de 80 px), para
 *    no dar tirones mientras lee algo anterior y el turno sigue llegando.
 *  - **Al preguntar**: cuando aparece un mensaje nuevo del usuario, se re-fija
 *    al fondo aunque estuviera leyendo arriba — acaba de escribir, quiere ver la
 *    respuesta. Se mira el último mensaje del usuario y no el último del hilo:
 *    la pregunta y el hueco de la respuesta de ARIA llegan en el mismo cambio,
 *    así que el último ya es de ARIA (medido en dev: la 2ª pregunta no bajaba).
 *  - **Al responder**: la respuesta se sigue hasta que su principio llega arriba
 *    del hilo; a partir de ahí se queda anclada en su primera línea y el usuario
 *    baja leyendo. Antes se seguía hasta el final y había que subir a buscar
 *    dónde empezaba. Se ancla una sola vez por respuesta: si luego el usuario
 *    baja al fondo, vuelve el comportamiento de "pegado".
 *
 * Para encontrar el principio de la respuesta, cada mensaje del hilo lleva
 * `data-message-id={msg.id}`.
 *
 * El movimiento es suave pero no usa `behavior: 'smooth'`: durante el streaming
 * `messages` cambia hasta ~60 veces por segundo y encadenar animaciones nativas
 * da tirones. En su lugar hay un único bucle de `requestAnimationFrame` que
 * persigue un destino que se puede mover (el fondo crece con cada token) con
 * frenada exponencial: sin reinicios entre chunks, se para solo al llegar.
 * Si el usuario mueve la rueda o arrastra durante la animación, gana él y el
 * bucle se corta. Con `prefers-reduced-motion` el salto es instantáneo.
 *
 * @param {Array} messages lista de mensajes del hilo; dispara el efecto al cambiar.
 * @returns {{ scrollContainerRef: object, handleScroll: Function }} para el
 *   contenedor scrollable: `ref={scrollContainerRef} onScroll={handleScroll}`.
 */
/** Separación entre el borde superior del hilo y la respuesta anclada. */
const ANCHOR_GAP = 12;
/** Fracción de la distancia restante que se recorre en cada frame de 60 Hz. */
const EASE_PER_FRAME = 0.18;

const prefersReducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;

export default function useStickToBottom(messages) {
  const scrollContainerRef = useRef(null);
  const isAtBottomRef = useRef(true);
  const anchoredIdRef = useRef(null);
  const lastUserIdRef = useRef(undefined);
  // Animación en curso: `target` devuelve el destino en cada frame (el fondo se
  // mueve mientras llega texto); `written` es el último scrollTop que puso el
  // bucle, para distinguir sus eventos de scroll de los del usuario.
  const animRef = useRef({ running: false, frame: null, target: null, written: null, last: 0 });

  const stopAnimation = useCallback(() => {
    const anim = animRef.current;
    if (anim.running && typeof cancelAnimationFrame === 'function') cancelAnimationFrame(anim.frame);
    anim.running = false;
    anim.target = null;
  }, []);

  const scrollTo = useCallback((el, target) => {
    const anim = animRef.current;
    const clamp = () => Math.max(0, Math.min(target(), el.scrollHeight - el.clientHeight));
    const settle = () => {
      isAtBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    };
    if (prefersReducedMotion()) {
      stopAnimation();
      el.scrollTop = clamp();
      settle();
      return;
    }
    anim.target = clamp;
    if (anim.running) return; // el bucle en marcha ya persigue el nuevo destino
    anim.running = true;
    anim.last = performance.now();
    const step = () => {
      if (!anim.target || !el.isConnected) {
        anim.running = false;
        return;
      }
      const now = performance.now();
      const dt = Math.min(Math.max(now - anim.last, 8), 50);
      anim.last = now;
      const to = anim.target();
      const diff = to - el.scrollTop;
      if (Math.abs(diff) < 1) {
        el.scrollTop = to;
        anim.target = null;
        anim.running = false;
        settle();
        return;
      }
      el.scrollTop += diff * (1 - (1 - EASE_PER_FRAME) ** (dt / 16.67));
      anim.written = el.scrollTop;
      anim.frame = requestAnimationFrame(step);
    };
    anim.frame = requestAnimationFrame(step);
  }, [stopAnimation]);

  const handleScroll = useCallback(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    const anim = animRef.current;
    if (anim.target) {
      if (Math.abs(el.scrollTop - anim.written) <= 2) return; // es nuestro propio scroll
      stopAnimation(); // el usuario ha tomado el control
    }
    isAtBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
  }, [stopAnimation]);

  useEffect(() => stopAnimation, [stopAnimation]);

  useEffect(() => {
    const last = messages?.[messages.length - 1];
    const el = scrollContainerRef.current;
    const toBottom = () => el.scrollHeight;

    let lastUserId = null;
    for (let i = (messages?.length ?? 0) - 1; i >= 0; i--) {
      if (messages[i]?.role === 'user') { lastUserId = messages[i].id ?? i; break; }
    }
    const isFirstRun = lastUserIdRef.current === undefined;
    if (lastUserId !== lastUserIdRef.current) {
      lastUserIdRef.current = lastUserId;
      if (!isFirstRun && lastUserId !== null) {
        isAtBottomRef.current = true;
        anchoredIdRef.current = null;
      }
    }

    if (!isAtBottomRef.current || !el) return;
    const bottom = el.scrollHeight - el.clientHeight;

    if (last?.id != null && anchoredIdRef.current !== last.id) {
      const node = [...el.querySelectorAll('[data-message-id]')]
        .find((n) => n.getAttribute('data-message-id') === String(last.id));
      if (node) {
        const top = node.getBoundingClientRect().top - el.getBoundingClientRect().top + el.scrollTop - ANCHOR_GAP;
        if (top < bottom) {
          anchoredIdRef.current = last.id;
          scrollTo(el, () => top);
          return;
        }
      }
    }

    // Ya anclada: si el usuario ha bajado al fondo, se le acompaña.
    if (anchoredIdRef.current === last?.id && animRef.current.target) return;
    scrollTo(el, toBottom);
  }, [messages, scrollTo]);

  return { scrollContainerRef, handleScroll };
}
